import { ArrowRight } from 'react-feather'
import clsx from 'clsx'
import { useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'

import { StatsBox } from './StatsBox'
import { getRedirectionLink } from 'utils/functions/getRedirectionLink'
import { useDashboardMetrics } from '../data'
import s from './Stats.module.css'

type Props = {
  companyId: string
  width: 'full' | 'half'
  hasNoProjects: boolean
  onCreateProject: () => void
}

const MINUTES_IN_A_DAY = 1440
const MINUTES_IN_AN_HOUR = 60
const HOURS_IN_A_DAY = 24

const Stats = ({
  companyId,
  width,
  hasNoProjects,
  onCreateProject,
}: Props): JSX.Element => {
  const history = useHistory()
  const { data: dashboardMetrics } = useDashboardMetrics(companyId)

  const getStatChange = () => {
    let reworkedItemChange = 0
    let approvalTimeChange = 0
    if (
      dashboardMetrics?.rework?.change &&
      dashboardMetrics?.average_approval_time?.change
    ) {
      reworkedItemChange = Math.abs(dashboardMetrics?.rework.change) ?? 0
      approvalTimeChange =
        Math.abs(dashboardMetrics?.average_approval_time.change) ?? 0
    }
    return { reworkedItemChange, approvalTimeChange }
  }
  const averageApprovalTimeChange = getStatChange().approvalTimeChange

  const displayedApprivalTimeChange =
    averageApprovalTimeChange > 0
      ? `Faster ${averageApprovalTimeChange.toFixed(1)}% 🚀`
      : `Slower ${averageApprovalTimeChange.toFixed(1)}% `

  const convertMinutes = (num: number) => {
    const days = Math.floor(num / MINUTES_IN_A_DAY)
    const hours = Math.floor((num % MINUTES_IN_A_DAY) / MINUTES_IN_AN_HOUR)
    const minutes = Math.floor(num % MINUTES_IN_A_DAY) % MINUTES_IN_AN_HOUR
    const onlyMinutes = days === 0 && hours === 0

    if (onlyMinutes && minutes === 0) {
      return 0
    }

    const minuteSuffix = minutes > 1 ? 'Minutes' : 'Minute'

    if (onlyMinutes && minutes < MINUTES_IN_AN_HOUR && minutes > 0)
      return `${minutes} ${minuteSuffix}`

    if (days === 0 && hours < HOURS_IN_A_DAY)
      return `${hours} ${hours > 1 ? 'hours' : 'hour'}`

    return `${days} ${days > 1 ? 'Days' : 'Day'}`
  }

  return (
    <div className={clsx(s.container, s[width])}>
      <div className={s.card}>
        <StatsBox
          title="Items Created"
          tooltipText="All items across projects you’ve created"
          value={dashboardMetrics?.total_assignments.value ?? 0}
          subInfo={
            <button
              className={s.link}
              onClick={() => history.push('/projects/items')}
            >
              View All <ArrowRight size={16} />
            </button>
          }
        />
        <StatsBox
          title="Approval Pending"
          tooltipText="All items across projects waiting for your approval"
          value={
            dashboardMetrics?.total_assignments_pending_approval.value ?? 0
          }
          subInfo={
            <button
              className={s.link}
              onClick={() => getRedirectionLink('approval_pending', history)}
            >
              View All <ArrowRight size={16} />
            </button>
          }
        />
        {width === 'full' && (
          <StatsBox
            statKey="average_approval_time"
            title="Average Approval Time"
            tooltipText="Average time taken to approve submissions delivered"
            value={convertMinutes(
              dashboardMetrics?.average_approval_time?.value ?? 0
            )}
            subInfo={
              <span
                className={clsx(s.changeGreen, {
                  [s.changeRed]: averageApprovalTimeChange < 0,
                  [s.zeroChange]: averageApprovalTimeChange === 0,
                })}
              >
                {averageApprovalTimeChange === 0
                  ? '0%'
                  : displayedApprivalTimeChange}
              </span>
            }
          />
        )}
      </div>
      {hasNoProjects && (
        <footer className={s.footer}>
          Create a project to start ordering content
          <button className={s.footerLink} onClick={onCreateProject}>
            Create Project <Icon icon="lucide:arrow-right" />
          </button>
        </footer>
      )}
      {width === 'half' && (
        <footer
          className={clsx(s.footer, {
            [s.red]: averageApprovalTimeChange < 0,
            [s.green]: averageApprovalTimeChange > 0,
          })}
        >
          {averageApprovalTimeChange === 0 && <span></span>}
          {averageApprovalTimeChange > 0 &&
            `Average Item approval time increased by ${displayedApprivalTimeChange}`}
          {averageApprovalTimeChange < 0 &&
            `Average Item approval time decreased by ${displayedApprivalTimeChange}`}
        </footer>
      )}
    </div>
  )
}

export default Stats
